import React, { useState, useEffect, useRef } from 'react';
import { FileText, Download, ChevronDown, ChevronUp } from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import { ScrollReveal } from '../components/ScrollReveal';
import './InstitucionalPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Convert Google Drive URLs to direct image URLs
const convertGoogleDriveUrl = (url) => {
  if (!url) return '';
  
  // Already a direct URL or lh3.googleusercontent.com
  if (url.includes('lh3.googleusercontent.com') || url.includes('thumbnail?id=')) {
    return url;
  }
  
  // Extract file ID from various Google Drive URL formats
  const patterns = [
    /\/file\/d\/([a-zA-Z0-9_-]+)/,
    /id=([a-zA-Z0-9_-]+)/,
    /\/d\/([a-zA-Z0-9_-]+)/
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return `https://drive.google.com/thumbnail?id=${match[1]}&sz=w1000`;
    }
  }
  
  return url;
};

const InstitucionalPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tabFromUrl = searchParams.get('tab') || 'missao';
  const [activeSection, setActiveSection] = useState(tabFromUrl);
  const [documents, setDocuments] = useState([]);
  const [cmsContent, setCmsContent] = useState({});
  const [loading, setLoading] = useState(true);
  
  const [socioForm, setSocioForm] = useState({
    nomeCompleto: '',
    dataNascimento: '',
    naturalidade: '',
    nacionalidade: '',
    estadoCivil: '',
    profissao: '',
    morada: '',
    codigoPostal: '',
    localidade: '',
    telefone: '',
    telemovel: '',
    email: '',
    nif: '',
    cartaoCidadao: '',
    iban: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [showSocioForm, setShowSocioForm] = useState(false);
  const [expandedAccordions, setExpandedAccordions] = useState([]);

  // Refs for scroll
  const missaoRef = useRef(null);
  const valoresRef = useRef(null);
  const infoGeralRef = useRef(null);
  const socioRef = useRef(null);
  const equipaRef = useRef(null);
  const sustentabilidadeRef = useRef(null);

  // Load CMS content
  useEffect(() => {
    const loadContent = async () => {
      try {
        const [contentRes, docsRes] = await Promise.all([
          axios.get(`${API}/cms/public/content/institucional`),
          axios.get(`${API}/public/documents`)
        ]);
        
        // Convert array to object keyed by section
        const contentMap = {};
        contentRes.data.forEach(item => {
          contentMap[item.section] = item.content;
        });
        setCmsContent(contentMap);
        setDocuments(docsRes.data);
      } catch (error) {
        console.error('Erro ao carregar conteúdo:', error);
      }
      setLoading(false);
    };
    loadContent();
  }, []);

  const handleSocioChange = (e) => {
    const { name, value } = e.target;
    setSocioForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSocioSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError(null);
    
    const submissionData = {
      nome_completo: socioForm.nomeCompleto,
      data_nascimento: socioForm.dataNascimento,
      naturalidade: socioForm.naturalidade,
      nacionalidade: socioForm.nacionalidade,
      estado_civil: socioForm.estadoCivil,
      profissao: socioForm.profissao,
      morada: socioForm.morada,
      codigo_postal: socioForm.codigoPostal,
      localidade: socioForm.localidade,
      telefone: socioForm.telefone,
      telemovel: socioForm.telemovel,
      email: socioForm.email,
      nif: socioForm.nif,
      cartao_cidadao: socioForm.cartaoCidadao,
      iban: socioForm.iban
    };
    
    try {
      await axios.post(`${API}/public/membership`, submissionData);
      setFormSubmitted(true);
      setTimeout(() => {
        setSocioForm({
          nomeCompleto: '',
          dataNascimento: '',
          naturalidade: '',
          nacionalidade: '',
          estadoCivil: '',
          profissao: '',
          morada: '',
          codigoPostal: '',
          localidade: '',
          telefone: '',
          telemovel: '',
          email: '',
          nif: '',
          cartaoCidadao: '',
          iban: ''
        });
        setFormSubmitted(false);
      }, 5000);
    } catch (err) {
      console.error('Erro ao enviar formulário:', err);
      setFormError('Ocorreu um erro ao enviar o formulário. Por favor, tente novamente.');
    } finally {
      setFormLoading(false);
    }
  };

  const scrollToSection = (sectionId) => {
    const refs = {
      'missao': missaoRef,
      'valores': valoresRef,
      'info-geral': infoGeralRef,
      'socio': socioRef,
      'equipa': equipaRef,
      'sustentabilidade': sustentabilidadeRef
    };
    
    const ref = refs[sectionId];
    if (ref?.current) {
      const yOffset = -120;
      const y = ref.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  useEffect(() => {
    if (tabFromUrl && !loading) {
      setTimeout(() => scrollToSection(tabFromUrl), 100);
    }
  }, [tabFromUrl, loading]);

  const navItems = [
    { id: 'missao', label: 'Missão e Visão' },
    { id: 'valores', label: 'Valores' },
    { id: 'info-geral', label: 'Informação Geral' },
    { id: 'equipa', label: 'Equipa' },
    { id: 'socio', label: 'Ser Sócio' },
    { id: 'sustentabilidade', label: 'Sustentabilidade' }
  ];

  // Get content with fallbacks
  const hero = cmsContent.hero || {};
  const missao = cmsContent.missao || {};
  const visao = cmsContent.visao || {};
  const valores = cmsContent.valores || {};
  const orgaos = cmsContent.orgaos_sociais || {};
  const equipa = cmsContent.equipa || {};
  const sustent = cmsContent.sustentabilidade || {};
  const compromissos = cmsContent.compromissos || {};

  if (loading) {
    return (
      <div className="institucional-page">
        <Header />
        <div style={{ padding: '200px 20px', textAlign: 'center', color: '#666' }}>
          A carregar...
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="institucional-page" data-testid="institucional-page">
      <SEO 
        title="Institucional"
        description="Conheça a AASPS - a nossa missão, visão, valores e história. Somos uma IPSS dedicada à promoção da saúde mental e bem-estar em Barcelos."
        keywords="AASPS, institucional, missão, visão, valores, história, IPSS, Barcelos"
        url="/institucional"
      />
      <Header />
      <Breadcrumbs />
      
      {/* Hero Section */}
      <div className="institucional-hero">
        <div className="institucional-hero-content">
          <h1>{hero.title || 'Sobre Nós'}</h1>
          <p>{hero.description || 'Na AASPS, trabalhamos para promover a saúde mental e o bem-estar, reduzir o estigma e aumentar o apoio às pessoas e comunidades que acompanhamos.'}</p>
        </div>
      </div>

      {/* Sub Navigation */}
      <nav className="sub-nav" data-testid="sub-nav">
        <div className="sub-nav-container">
          {navItems.map((item) => (
            <button
              key={item.id}
              className={`sub-nav-link ${activeSection === item.id ? 'active' : ''}`}
              onClick={() => scrollToSection(item.id)}
              data-testid={`nav-${item.id}`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </nav>

      {/* Missão Section */}
      <section ref={missaoRef} id="missao" className="media-content-section bg-white">
        <div className="media-content-container">
          <div className="media-content-image">
            <img 
              src={missao.image || "https://images.pexels.com/photos/7579310/pexels-photo-7579310.jpeg"} 
              alt="Equipa AASPS em reunião de apoio" 
            />
          </div>
          <div className="media-content-text">
            <h2>{missao.title || 'A Nossa Missão'}</h2>
            <p>{missao.text1 || 'Promover a saúde mental e o bem-estar de crianças, jovens e adultos, reforçar a literacia em saúde e implementar medidas eficazes contra o estigma e a discriminação.'}</p>
            {missao.text2 && <p>{missao.text2}</p>}
          </div>
        </div>
      </section>

      {/* Visão Section */}
      <section className="media-content-section bg-grey">
        <div className="media-content-container reverse">
          <div className="media-content-image">
            <img 
              src={visao.image || "https://customer-assets.emergentagent.com/job_c102441f-4f72-442c-a938-361b394b0754/artifacts/h0zfqzeb_AE128281-F14E-4865-B146-99F892BE3763_1_105_c.jpeg"} 
              alt="A Nossa Visão" 
            />
          </div>
          <div className="media-content-text visao-text">
            <h2>{visao.title || 'A Nossa Visão'}</h2>
            <p>{visao.text || 'Acreditamos numa sociedade que reconhece o valor e a dignidade de cada cidadão, garantindo que ninguém é definido pela sua condição. Acreditamos que a participação social e a autonomia são pilares essenciais para uma vida plena, e é por isso que desenvolvemos respostas que promovem mais saúde mental para todos.'}</p>
          </div>
        </div>
      </section>

      {/* Valores Section */}
      <section ref={valoresRef} id="valores" className="values-section">
        <div className="values-container">
          <div className="values-header">
            <h2>{valores.title || 'Os Nossos Valores'}</h2>
            <p>{valores.subtitle || 'Princípios que orientam a nossa intervenção'}</p>
          </div>
          <div className="values-grid">
            {(valores.items || [
              { title: 'Respeito e Dignidade', description: 'Reconhecemos o valor intrínseco de cada pessoa e promovemos relações assentes na empatia e na valorização das suas características únicas.' },
              { title: 'Igualdade e Equidade', description: 'Defendemos oportunidades justas para todos, combatemos desigualdades e incentivamos a inclusão em todos os contextos da vida social.' },
              { title: 'Solidariedade', description: 'Fazemos da responsabilidade coletiva um princípio orientador para fortalecer os laços comunitários e promover o bem comum.' },
              { title: 'Direitos Humanos', description: 'Asseguramos a defesa e a promoção dos direitos fundamentais, garantindo liberdade, justiça e proteção da dignidade humana.' },
              { title: 'Justiça Social', description: 'Trabalhamos por uma sociedade mais justa onde cada pessoa possa exercer plenamente os seus direitos.' },
              { title: 'Transparência', description: 'Atuamos com integridade e clareza em todas as nossas ações, mantendo uma comunicação aberta com a comunidade.' }
            ]).map((valor, idx) => (
              <div className="value-card" key={idx} data-testid={`value-card-${idx + 1}`}>
                <h3>{valor.title}</h3>
                <p>{valor.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Informação Geral Section */}
      <section ref={infoGeralRef} id="info-geral" className="info-geral-section">
        <div className="info-geral-container">
          <div className="info-geral-header">
            <h2>{orgaos.title || 'Órgãos Sociais'}</h2>
          </div>
          
          <div className="orgaos-intro">
            <p><strong>Quadriénio 2022–2025</strong></p>
            <p>Nos termos dos Estatutos, a AASPS é composta pelos seguintes órgãos sociais:</p>
          </div>

          <div className="orgaos-grid">
            {(orgaos.orgaos || [
              { nome: 'Assembleia-Geral', membros: [{ cargo: 'Presidente', nome: "Maria Madalena O'Neill" }, { cargo: 'Vice-Presidente', nome: 'Andreia Filipa Malhadas Porfírio' }, { cargo: 'Secretário', nome: 'Luís Miguel Baptista Anselmo' }] },
              { nome: 'Direção', membros: [{ cargo: 'Presidente', nome: 'Luís M Porfírio' }, { cargo: 'Vice-Presidente', nome: 'Carla Quintas Fernandes' }, { cargo: 'Tesoureiro', nome: 'Irene Cabral Ramos Fortunato Teles' }, { cargo: 'Secretário', nome: 'João Carlos Martinho da Silva' }, { cargo: 'Vogal', nome: 'Irene Luísa da Conceição Lopes' }] },
              { nome: 'Conselho Fiscal', membros: [{ cargo: 'Presidente', nome: 'Emídio António Cabrita Fernandes' }, { cargo: 'Secretário', nome: 'David Afonso da Costa Ferreira' }, { cargo: 'Vogal', nome: 'Margarida Santos Pereira Landeiro' }] }
            ]).map((orgao, idx) => (
              <div className="orgao-card" key={idx} data-testid={`orgao-${idx}`}>
                <h3>{orgao.nome}</h3>
                {orgao.membros.map((membro, midx) => (
                  <div key={midx} className="membro-item">
                    <span className="membro-cargo">{membro.cargo}</span>
                    <span className="membro-nome">{membro.nome}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Documents Section - Discrete Button */}
          <div className="documentos-section" data-testid="documentos-section">
            <button 
              className={`documentos-btn ${expandedAccordions.includes('documentos') ? 'expanded' : ''}`}
              onClick={() => setExpandedAccordions(prev => 
                prev.includes('documentos') 
                  ? prev.filter(id => id !== 'documentos')
                  : [...prev, 'documentos']
              )}
            >
              Documentos
              {expandedAccordions.includes('documentos') ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
            
            {expandedAccordions.includes('documentos') && (
              <div className="documentos-expanded">
                {/* Main Documents */}
                {documents.filter(d => d.category === 'principais' && d.is_active).length > 0 && (
                  <div className="docs-section-group">
                    <h4>Documentos Principais</h4>
                    <div className="docs-list-simple">
                      {documents
                        .filter(d => d.category === 'principais' && d.is_active)
                        .sort((a, b) => a.order - b.order)
                        .map(doc => (
                          <a 
                            key={doc.id}
                            href={doc.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="doc-link-simple"
                          >
                            <FileText size={16} />
                            {doc.title}
                          </a>
                        ))}
                    </div>
                  </div>
                )}

                {/* Planos de Atividades */}
                {documents.filter(d => d.category === 'planos' && d.is_active).length > 0 && (
                  <div className="docs-section-group">
                    <h4>Planos de Atividades</h4>
                    <div className="docs-list-simple">
                      {documents
                        .filter(d => d.category === 'planos' && d.is_active)
                        .sort((a, b) => a.order - b.order)
                        .map(doc => (
                          <a 
                            key={doc.id}
                            href={doc.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="doc-link-simple"
                          >
                            <FileText size={16} />
                            {doc.title}
                          </a>
                        ))}
                    </div>
                  </div>
                )}

                {/* Regulamentos Internos */}
                {documents.filter(d => d.category === 'regulamentos' && d.is_active).length > 0 && (
                  <div className="docs-section-group">
                    <h4>Regulamentos Internos</h4>
                    <div className="docs-list-simple">
                      {documents
                        .filter(d => d.category === 'regulamentos' && d.is_active)
                        .sort((a, b) => a.order - b.order)
                        .map(doc => (
                          <a 
                            key={doc.id}
                            href={doc.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="doc-link-simple"
                          >
                            <FileText size={16} />
                            {doc.title}
                          </a>
                        ))}
                    </div>
                  </div>
                )}

                {/* Relatórios */}
                {documents.filter(d => d.category === 'relatorios' && d.is_active).length > 0 && (
                  <div className="docs-section-group">
                    <h4>Relatórios de Atividades e Contas</h4>
                    <div className="docs-list-simple">
                      {documents
                        .filter(d => d.category === 'relatorios' && d.is_active)
                        .sort((a, b) => a.order - b.order)
                        .map(doc => (
                          <a 
                            key={doc.id}
                            href={doc.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="doc-link-simple"
                          >
                            <FileText size={16} />
                            {doc.title}
                          </a>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Equipa Section */}
      <section ref={equipaRef} id="equipa" className="equipa-section">
        <div className="equipa-container">
          <div className="equipa-header">
            <h2>{equipa.title || 'A Nossa Equipa'}</h2>
            <p>{equipa.description || 'Conheça os profissionais que fazem parte da AASPS'}</p>
          </div>
          
          <div className="equipa-content-wrapper">
            <div className="equipa-image">
              <img 
                src={convertGoogleDriveUrl(equipa.membros?.[0]?.foto) || 'https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&w=800'} 
                alt="A Nossa Equipa" 
              />
            </div>
            <div className="equipa-cta">
              <h3>Junte-se a nós</h3>
              <p>Faça parte da nossa missão.</p>
              <div className="equipa-buttons">
                <button 
                  onClick={() => navigate('/carreiras')} 
                  className="equipa-btn primary"
                  data-testid="candidaturas-btn"
                >
                  Candidaturas
                </button>
                <a href="/contactos" className="equipa-btn secondary">Contactar</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Parceiros Section */}
      <section className="parceiros-section" data-testid="parceiros-section">
        <div className="parceiros-container">
          <h2>Parceiros</h2>
          <p className="parceiros-subtitle">Instituições que colaboram connosco na promoção da saúde mental e do bem-estar comunitário.</p>
          <div className="parceiros-grid">
            <div className="parceiro-item" data-testid="parceiro-seg-social" title="Segurança Social">
              <img src="/partners/segsocial.png" alt="Segurança Social" loading="lazy" />
            </div>
            <div className="parceiro-item" data-testid="parceiro-cml" title="Câmara Municipal de Lisboa">
              <img src="/partners/cml.png" alt="Câmara Municipal de Lisboa" loading="lazy" />
            </div>
            <div className="parceiro-item" data-testid="parceiro-opp" title="Ordem dos Psicólogos Portugueses">
              <img src="/partners/opp.png" alt="Ordem dos Psicólogos Portugueses" loading="lazy" />
            </div>
            <div className="parceiro-item" data-testid="parceiro-ispa" title="ISPA – Instituto Universitário">
              <img src="/partners/ispa.png" alt="ISPA – Instituto Universitário" loading="lazy" />
            </div>
            <div className="parceiro-item" data-testid="parceiro-iscte" title="ISCTE – Instituto Universitário de Lisboa">
              <img src="/partners/iscte.png" alt="ISCTE – Instituto Universitário de Lisboa" loading="lazy" />
            </div>
            <div className="parceiro-item" data-testid="parceiro-lusofona" title="Universidade Lusófona">
              <img src="/partners/ulusofona.svg" alt="Universidade Lusófona" loading="lazy" />
            </div>
            <div className="parceiro-item" data-testid="parceiro-lusiada" title="Universidade Lusíada de Lisboa">
              <img src="/partners/lusiada.png" alt="Universidade Lusíada de Lisboa" loading="lazy" />
            </div>
            <div className="parceiro-item" data-testid="parceiro-jf-ajuda" title="Junta de Freguesia da Ajuda">
              <img src="/partners/jfajuda.jpg" alt="Junta de Freguesia da Ajuda" loading="lazy" />
            </div>
            <div className="parceiro-item" data-testid="parceiro-gulbenkian" title="Fundação Calouste Gulbenkian">
              <img src="/partners/gulbenkian.png" alt="Fundação Calouste Gulbenkian" loading="lazy" />
            </div>
            <div className="parceiro-item" data-testid="parceiro-bipzip" title="Programa BIP/ZIP — Câmara Municipal de Lisboa">
              <img src="/partners/bipzip.png" alt="BIP/ZIP — Bairros e Zonas de Intervenção Prioritária de Lisboa" loading="lazy" />
            </div>
            <div className="parceiro-item" data-testid="parceiro-fundacao-edp" title="Fundação EDP">
              <img src="/partners/fundacao-edp.jpg" alt="Fundação EDP" loading="lazy" />
            </div>
            <div className="parceiro-item" data-testid="parceiro-ccb" title="CCB — Centro Cultural de Belém / Fábrica das Artes">
              <img src="/partners/ccb.svg" alt="CCB — Centro Cultural de Belém" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Sustentabilidade Section */}
      <section ref={sustentabilidadeRef} id="sustentabilidade" className="media-content-section bg-white">
        <div className="media-content-container">
          <div className="media-content-image">
            <img 
              src="https://customer-assets.emergentagent.com/job_d1f4b64a-9147-417f-92a6-cafceec850a0/artifacts/0kpgm19z_act2.jpeg" 
              alt="Atividade comunitária de sustentabilidade" 
            />
          </div>
          <div className="media-content-text">
            <h2>Sustentabilidade</h2>
            <p>Alinhamos o nosso trabalho com os 17 ODS, promovemos a inclusão, a redução das desigualdades e a resiliência comunitária — pilares do bem-estar e da saúde mental. Inspirados nas cinco dimensões da Agenda 2030, traduzimos estes princípios em ações que fortalecem redes de apoio, incentivam sustentabilidade e criam oportunidades de desenvolvimento com autonomia e dignidade. O nosso objetivo: uma comunidade mais sustentável, justa e promotora de bem-estar para todas as pessoas.</p>
          </div>
        </div>
      </section>

      {/* Compromissos Section */}
      <section className="sustentabilidade-section">
        <div className="sustentabilidade-container">
          <div className="sustentabilidade-header">
            <h2>{compromissos.title || 'Os Nossos Compromissos'}</h2>
            <p>A sustentabilidade está no centro da nossa estratégia.</p>
          </div>
          
          <div className="compromissos-grid">
            {(compromissos.items || [
              'Envolver, de forma inclusiva e participativa, a comunidade, os seus beneficiários, colaboradores e parceiros na definição e avaliação das ações.',
              'Criar, promover e partilhar conhecimento técnico e científico sobre saúde mental, inclusão social, cidadania e bem-estar.',
              'Assumir a proteção do ambiente e integrar princípios de responsabilidade social no planeamento estratégico.',
              'Melhorar o impacte ambiental, promovendo práticas responsáveis e reduzindo consumos e resíduos.',
              'Gerir de forma responsável os aspetos sociais, promovendo ações de prevenção e defesa dos direitos.',
              'Avaliar e melhorar continuamente o desempenho, promovendo uma cultura de aprendizagem permanente.'
            ]).map((item, idx) => (
              <div className="compromisso-item" key={idx}>{item}</div>
            ))}
          </div>
        </div>
      </section>

      {/* Ser Sócio Section */}
      <section ref={socioRef} id="socio" className="socio-section">
        <div className="socio-container">
          <div className="socio-header">
            <h2>Ser Sócio</h2>
            <p>Junte-se a nós e faça parte desta missão</p>
          </div>

          <div className="socio-info">
            <h3>Documentos Necessários</h3>
            <p>
              Para que a Proposta de Sócio seja considerada válida, deverá estar devidamente preenchida, sendo ainda obrigatória a apresentação dos seguintes documentos: documento de identificação e Número de Identificação Bancária (NIB).
            </p>
            
            <h3>Jóia e Quota de Associado</h3>
            <p>
              O valor da quota anual é <strong>5,00 € (cinco euros)</strong>, a liquidar no momento da inscrição. 
              O pagamento da anuidade do ano seguinte deve ser efetuado por Débito Direto.
            </p>
          </div>

          {formSubmitted ? (
            <div className="form-success" data-testid="form-success">
              <p>Obrigado pela sua inscrição! Entraremos em contacto brevemente.</p>
            </div>
          ) : !showSocioForm ? (
            <div className="socio-cta">
              <button 
                className="cta-button" 
                onClick={() => setShowSocioForm(true)}
                data-testid="show-form-btn"
              >
                Inscrever-me como Sócio
              </button>
            </div>
          ) : (
            <form className="socio-form" onSubmit={handleSocioSubmit} data-testid="socio-form">
              <h3>Formulário de Inscrição</h3>
              
              <div className="form-row">
                <div className="form-group full-width">
                  <label htmlFor="nomeCompleto">Nome Completo *</label>
                  <input type="text" id="nomeCompleto" name="nomeCompleto" value={socioForm.nomeCompleto} onChange={handleSocioChange} required data-testid="input-nome" />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="dataNascimento">Data de Nascimento *</label>
                  <input type="date" id="dataNascimento" name="dataNascimento" value={socioForm.dataNascimento} onChange={handleSocioChange} required />
                </div>
                <div className="form-group">
                  <label htmlFor="naturalidade">Naturalidade</label>
                  <input type="text" id="naturalidade" name="naturalidade" value={socioForm.naturalidade} onChange={handleSocioChange} />
                </div>
                <div className="form-group">
                  <label htmlFor="nacionalidade">Nacionalidade *</label>
                  <input type="text" id="nacionalidade" name="nacionalidade" value={socioForm.nacionalidade} onChange={handleSocioChange} required />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="estadoCivil">Estado Civil</label>
                  <select id="estadoCivil" name="estadoCivil" value={socioForm.estadoCivil} onChange={handleSocioChange}>
                    <option value="">Selecione...</option>
                    <option value="solteiro">Solteiro(a)</option>
                    <option value="casado">Casado(a)</option>
                    <option value="uniao">União de Facto</option>
                    <option value="divorciado">Divorciado(a)</option>
                    <option value="viuvo">Viúvo(a)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="profissao">Profissão</label>
                  <input type="text" id="profissao" name="profissao" value={socioForm.profissao} onChange={handleSocioChange} />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group full-width">
                  <label htmlFor="morada">Morada *</label>
                  <input type="text" id="morada" name="morada" value={socioForm.morada} onChange={handleSocioChange} required />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="codigoPostal">Código Postal *</label>
                  <input type="text" id="codigoPostal" name="codigoPostal" placeholder="0000-000" value={socioForm.codigoPostal} onChange={handleSocioChange} required />
                </div>
                <div className="form-group">
                  <label htmlFor="localidade">Localidade *</label>
                  <input type="text" id="localidade" name="localidade" value={socioForm.localidade} onChange={handleSocioChange} required />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="telefone">Telefone</label>
                  <input type="tel" id="telefone" name="telefone" value={socioForm.telefone} onChange={handleSocioChange} />
                </div>
                <div className="form-group">
                  <label htmlFor="telemovel">Telemóvel *</label>
                  <input type="tel" id="telemovel" name="telemovel" value={socioForm.telemovel} onChange={handleSocioChange} required />
                </div>
                <div className="form-group">
                  <label htmlFor="email">E-mail *</label>
                  <input type="email" id="email" name="email" value={socioForm.email} onChange={handleSocioChange} required />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="nif">NIF *</label>
                  <input type="text" id="nif" name="nif" value={socioForm.nif} onChange={handleSocioChange} required />
                </div>
                <div className="form-group">
                  <label htmlFor="cartaoCidadao">N.º Cartão de Cidadão *</label>
                  <input type="text" id="cartaoCidadao" name="cartaoCidadao" value={socioForm.cartaoCidadao} onChange={handleSocioChange} required />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group full-width">
                  <label htmlFor="iban">IBAN (para Débito Direto) *</label>
                  <input type="text" id="iban" name="iban" placeholder="PT50 0000 0000 0000 0000 0000 0" value={socioForm.iban} onChange={handleSocioChange} required />
                </div>
              </div>
              
              <div className="form-consent">
                <label>
                  <input type="checkbox" required />
                  <span>Declaro que tomei conhecimento e aceito os termos e condições de associado, bem como a política de privacidade da AASPS.</span>
                </label>
              </div>
              
              <div className="form-submit">
                {formError && <div className="form-error">{formError}</div>}
                <button type="submit" className="cta-button" disabled={formLoading} data-testid="submit-btn">
                  {formLoading ? 'A enviar...' : 'Submeter Inscrição'}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
      
      <Footer />
      )}
    </div>
  );
};

export default InstitucionalPage;
